import { z } from 'zod';
import { OrderInsertFeeWithServiceType, ProcessedOrderInsertFee } from './sales-orders.types';

export enum ScenarioTypes {
  LCC_OPTION = 'LCC_OPTION', // cheapest option with the higher difference_amount
  ALTERNATIVE = 'ALTERNATIVE', // additional options we've identified
}

export enum SalesOrderErrorStatus {
  UNREVIEWED = 'UNREVIEWED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  SENT = 'SENT',
  NO_REVIEW_REQUIRED = 'NO_REVIEW_REQUIRED',
}

export enum ServiceType {
  PICK_N_PACK = 'PICK_N_PACK',
  FREIGHT = 'FREIGHT',
  WHOLESALE_FREIGHT = 'WHOLESALE_FREIGHT',
}

export enum FreightChargeType {
  WHOLESALE_BASE_FREIGHT = 'Base Freight - LTL/FTL',
}

export enum SalesOrderStatus {
  UNREVIEWED = 'UNREVIEWED',
  REVIEWED = 'REVIEWED',
  NO_REVIEW_REQUIRED = 'NO_REVIEW_REQUIRED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  CREDITS_PARTIALLY_REQUESTED = 'CREDITS_PARTIALLY_REQUESTED',
  CREDITS_REQUESTED = 'CREDITS_REQUESTED',
}

export enum SalesOrderStatusByServiceType {
  UNREVIEWED = 'UNREVIEWED',
  REVIEWED_ACCEPTED = 'REVIEWED_ACCEPTED',
  REVIEWED_REJECTED = 'REVIEWED_REJECTED',
  NO_REVIEW_REQUIRED = 'NO_REVIEW_REQUIRED',
  CREDITS_REQUESTED = 'CREDITS_REQUESTED',
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum SortBy {
  SALES_ORDER_NAME = 'SALES_ORDER_NAME',
  STATUS = 'STATUS',
  ERROR_TYPES = 'ERROR_TYPES',
  SERVICES = 'SERVICES',
  TOTAL_ERROR_DOLLARS = 'TOTAL_ERROR_DOLLARS',
  TOTAL_ERROR_COUNT = 'TOTAL_ERROR_COUNT',
  TOTAL_UNFAVORABLE_DOLLARS = 'TOTAL_UNFAVORABLE_DOLLARS',
  TOTAL_FAVORABLE_DOLLARS = 'TOTAL_FAVORABLE_DOLLARS',
  TOTAL_UNFAVORABLE_COUNT = 'TOTAL_UNFAVORABLE_COUNT',
  TOTAL_FAVORABLE_COUNT = 'TOTAL_FAVORABLE_COUNT',
}

export enum ErrorGroupSortBy {
  INVOICE_NUMBER = 'INVOICE_NUMBER',
  SALES_ORDER_NAME = 'SALES_ORDER_NAME',
  STATUS = 'STATUS',
  SERVICES = 'SERVICES',
  TOTAL_ERROR_DOLLARS = 'TOTAL_ERROR_DOLLARS',
  UNITS = 'UNITS',
  QUANTITY = 'QUANTITY',
  EXPECTED_TOTAL = 'EXPECTED_TOTAL',
  INVOICED_TOTAL = 'INVOICED_TOTAL',
}

export enum DefaultErrorGroups {
  ALL_ORDERS = 'ALL_ORDERS',
  ALL_NON_CREDITABLE = 'ALL_NON_CREDITABLE',
  ALL_UNMATCHING_ORDERS = 'ALL_UNMATCHING_ORDERS',
}

export enum QueryType {
  STATUS = 'STATUS',
  ERROR_TYPES = 'ERROR_TYPES',
  SERVICES = 'SERVICES',
}

export enum QueryLinker {
  AND = 'AND',
  OR = 'OR',
}

export const SalesOrderQuerySchema = z.object({
  pageSize: z.number().int().min(1).max(200).default(30),
  page: z.number().int().min(1).default(1),
  sort: z.nativeEnum(SortOrder).default(SortOrder.ASC),
  sortBy: z.nativeEnum(SortBy).default(SortBy.SALES_ORDER_NAME),
  salesOrderNumberSearch: z.string().optional(),
  query: z.array(z.object({ type: z.nativeEnum(QueryType), value: z.string() })).optional(),
  linkers: z.array(z.nativeEnum(QueryLinker)).optional(),
});

export const SalesOrderErrorGroupQuerySchema = z.object({
  pageSize: z.number().int().min(1).max(200).default(30),
  page: z.number().int().min(1).default(1),
  sort: z.nativeEnum(SortOrder).default(SortOrder.ASC),
  sortBy: z.nativeEnum(ErrorGroupSortBy).default(ErrorGroupSortBy.SALES_ORDER_NAME),
  salesOrderNumberSearch: z.string().optional(),
  errorGroup: z.string().or(z.nativeEnum(DefaultErrorGroups)).optional(),
  statuses: z.array(z.nativeEnum(SalesOrderStatusByServiceType)),
});

export const SalesOrderErrorGroupQueryV2Schema = SalesOrderErrorGroupQuerySchema.extend({
  // Mode 1: Single invoice (backward compatible)
  invoiceNumber: z.string().optional(),

  // Mode 2: Monthly view
  month: z.string().transform(Number).pipe(z.number().int().min(1).max(12)).optional(),
  year: z.string().transform(Number).pipe(z.number().int().min(1970)).optional(),

  // Mode 3: Custom date range
  startDate: z.string().date().optional(),
  endDate: z.string().date().optional(),

  // Optional filter by service type (PICK_N_PACK, FREIGHT, etc)
  serviceType: z.string().optional(),

  // Optional filters for multi-invoice modes only
  logisticsProviderName: z.string().optional(),
  types: z.array(z.string()).optional(),
})
  .refine(
    (data) => {
      // Exactly one mode must be specified
      const hasInvoiceMode = !!data.invoiceNumber;
      const hasMonthlyMode = !!(data.month && data.year);
      const hasCustomMode = !!(data.startDate && data.endDate);

      const modes = [hasInvoiceMode, hasMonthlyMode, hasCustomMode];
      const activeModes = modes.filter(Boolean).length;

      return activeModes === 1;
    },
    {
      message: 'Exactly one mode must be specified: invoiceNumber OR (month AND year) OR (startDate AND endDate)',
    },
  )
  .refine(
    (data) => {
      // logisticsProviderName and types filters only allowed in multi-invoice modes
      const hasSingleInvoiceMode = !!data.invoiceNumber;
      const hasFilterFields = !!(data.logisticsProviderName || data.types?.length);

      if (hasSingleInvoiceMode && hasFilterFields) {
        return false;
      }

      return true;
    },
    {
      message:
        'logisticsProviderName and types filters can only be used with monthly or date range modes, not with single invoice queries',
    },
  );

export const ErrorGroupFilterSchema = z.object({
  totalCreditableErrorsDollars: z.number(),
  totalOrdersCount: z.number(),
  ErrorGroups: z.array(
    z.object({
      errorGroupName: z.string(),
      errorRootCauses: z.array(
        z.object({
          rootCause: z.string(),
          errorDescription: z.string(),
        }),
      ),
      orderNum: z.number(),
      creditableErrorsDollars: z.number(),
      creditableErrorsCount: z.number(),
      reviewableOrderCount: z.number(),
    }),
  ),
});

export const InvoiceErrorGroupSalesOrderSchema = z.object({
  invoiceNumber: z.string(),
  salesOrderNumber: z.string(),
  status: z.nativeEnum(SalesOrderStatusByServiceType),
  packageType: z.string().nullable(),
  serviceLevel: z.string().nullable(),
  quantity: z.number().nullable(),
  expectedTotal: z.number().nullable(),
  invoicedTotal: z.number().nullable(),
  services: z.array(z.string()),
  creditableErrorsDollars: z.number(),
});

export const InvoiceSalesOrderSchema = z.object({
  salesOrderNumber: z.string(),
  status: z.nativeEnum(SalesOrderStatus),
  errorType: z.array(z.string()),
  services: z.array(z.string()),
  totalErrorDollars: z.number(),
  totalErrorsCount: z.number(),
  favorableTotalCount: z.number(),
  unfavorableTotalCount: z.number(),
  favorableDollarsTotal: z.number(),
  unfavorableDollarsTotal: z.number(),
  creditableErrorsDollars: z.number(),
  creditableErrorsCount: z.number(),
  totalAcceptedErrors: z.number(),
  totalRejectedErrors: z.number(),
  totalUnreviewedErrors: z.number(),
  totalSentErrors: z.number(),
});

export const QueryMetaDataSchema = z.object({
  total: z.number(),
  totalFiltered: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPageCount: z.number(),
});

export const SalesOrderErrorsSchema = z.object({
  salesOrderNumber: z.string(),
  invoiceNumber: z.string(),
  brandId: z.string(),
  status: z.nativeEnum(SalesOrderErrorStatus),
  errorType: z.string(),
  totalErrorDollars: z.number(),
  unfavorableDollars: z.number(),
  favorableDollars: z.number(),
  unfavorableCount: z.number(),
  favorableCount: z.number(),
  totalErrorCount: z.number(),
  services: z.array(z.string()),
  serviceType: z.nativeEnum(ServiceType),
});

export const SalesOrderMetadataSchema = z.object({
  carrier: z.string().optional(),
  serviceLevel: z.string().optional(),
  virtualCarrier: z.string().optional(),
  virtualCarrierServiceLevel: z.string().optional(),
  zone: z.coerce.string().optional(),
  actualWeight: z.coerce.number().optional(),
  roundedWeight: z.coerce.number().optional(),
  dimensionalWeight: z.coerce.number().optional(),
  billedWeight: z.coerce.number().optional(),
  zipcode: z.coerce.string().optional(),
  shipToCity: z.string().optional(),
  shipToState: z.string().optional(),
  recipient: z.string().optional(),
  packageType: z.string().optional(),
  isNoSalesOrderMatch: z.boolean().optional(),
});

// Shared schema for freight details
export const FreightDetailsSchema = z.object({
  carrier: z.string().nullable(),
  serviceLevel: z.string().nullable(),
  virtualCarrier: z.string().nullable(),
  virtualCarrierServiceLevel: z.string().nullable(),
  zone: z.string().nullable(),
  actualWeight: z.number().nullable(),
  roundedWeight: z.number().nullable(),
  dimensionalWeight: z.number().nullable(),
  billedWeight: z.number().nullable(),
  zipcode: z.string().nullable(),
  recipient: z.string().nullable(),
  shipToCity: z.string().nullable(),
  shipToState: z.string().nullable(),
  packageType: z.string().nullable(),
});

// Extended freight details with additional fields for OrderOverview
export const ExtendedFreightDetailsSchema = FreightDetailsSchema.extend({
  isCubic: z.boolean().nullable(),
  isRural: z.boolean().nullable(),
});

export const SalesOrderSchema = z.object({
  id: z.string(),
  salesOrderNumber: z.string(),
  brandId: z.string(),
  status: z.nativeEnum(SalesOrderStatus),
  invoiceNumber: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  invoicedMetadata: SalesOrderMetadataSchema.optional(),
  expectedMetadata: SalesOrderMetadataSchema.optional(),
  isInvoiceRollupItem: z.boolean().optional(),
});

export const OrderOverviewSchema = z.object({
  invoiceNumber: z.string(),
  reconcilationLevel: z.string().optional(),
  orderFulfilmentDetail: z.object({
    logisticsProvider: z.string(),
    warehouse: z.string(),
  }),
  orderTimeline: z.array(
    z.object({
      date: z.date().nullable(),
      event: z.enum(['Order Date', 'Fulfilment Date', 'Ship Date']),
    }),
  ),
  freight: z.object({
    expected: z.object({
      total: z.object({
        cost: z.number(),
      }),
      serviceFees: z.array(
        z.object({
          serviceName: z.string(),
          qty: z.number(),
          rate: z.number().optional(),
          totalCost: z.number(),
          skus: z.array(
            z.object({
              name: z.string(),
              qty: z.number(),
              totalCost: z.number(),
            }),
          ),
        }),
      ),
    }),
    invoiced: z.object({
      total: z.object({
        cost: z.number(),
      }),
      serviceFees: z.array(
        z.object({
          serviceName: z.string(),
          qty: z.number(),
          rate: z.number().optional(),
          totalCost: z.number(),
          skus: z.array(
            z.object({
              name: z.string(),
              qty: z.number(),
              totalCost: z.number(),
            }),
          ),
        }),
      ),
    }),
    charges: z.array(
      z.object({
        errorType: z.string().nullable(),
        errorDifference: z.number(),
        expectedTotalCharges: z.number(),
        invoicedTotalCharges: z.number(),
        service: z.string(),
        isConstituent: z.boolean().optional(),
        invoicedQuantity: z.number(),
        invoicedRate: z.number(),
        expectedQuantity: z.number(),
        expectedRate: z.number(),
      }) satisfies z.ZodType<ProcessedOrderInsertFee>,
    ),
    totalFreight: z.object({
      expectedTotalCharges: z.number().nullable(),
      invoicedTotalCharges: z.number().nullable(),
      differencesTotal: z.number().nullable(),
      errorsTotal: z.number(),
    }),
    details: ExtendedFreightDetailsSchema,
  }),
  pickAndPack: z.object({
    expected: z.object({
      total: z.object({
        cost: z.number(),
      }),
      serviceFees: z.array(
        z.object({
          serviceName: z.string(),
          qty: z.number(),
          rate: z.number(),
          totalCost: z.number(),
          skus: z.array(
            z.object({
              name: z.string(),
              qty: z.number(),
              rate: z.number().optional(),
              totalCost: z.number(),
            }),
          ),
        }),
      ),
    }),
    invoiced: z.object({
      total: z.object({
        cost: z.number(),
      }),
      serviceFees: z.array(
        z.object({
          serviceName: z.string(),
          qty: z.number(),
          rate: z.number(),
          totalCost: z.number(),
          skus: z.array(
            z.object({
              name: z.string(),
              qty: z.number(),
              rate: z.number().optional(),
              totalCost: z.number(),
            }),
          ),
        }),
      ),
    }),
    // Deprecated, will be removed once Order Details 2.0 goes live
    lineItemDetails: z.array(
      z.object({
        isConstituent: z.boolean().default(false).optional(),
        errorType: z.string().nullable(),
        expectedQuantity: z.number().nullable(),
        expectedRate: z.number().nullable(),
        expectedTotalCharges: z.number().nullable(),
        invoicedQuantity: z.number().nullable(),
        invoicedRate: z.number().nullable(),
        invoicedTotalCharges: z.number().nullable(),
        errorDifference: z.number().nullable(),
        item: z.string(),
        productName: z.string().nullable(),
        service: z.string(),
      }),
    ),
    // Deprecated, will be removed once Order Details 2.0 goes live
    orderInsertFees: z.array(
      z.object({
        isConstituent: z.boolean().default(false).optional(),
        errorType: z.string().nullable(),
        expectedQuantity: z.number().nullable(),
        expectedRate: z.number().nullable(),
        expectedTotalCharges: z.number().nullable(),
        invoicedQuantity: z.number().nullable(),
        invoicedRate: z.number().nullable(),
        invoicedTotalCharges: z.number().nullable(),
        errorDifference: z.number().nullable(),
        service: z.string(),
      }),
    ),
    // Deprecated, will be removed once Order Details 2.0 goes live
    totalOrderLevelFees: z.object({
      expectedQuantity: z.number().nullable(),
      expectedRate: z.number().nullable(),
      invoicedQuantity: z.number().nullable(),
      invoicedRate: z.number().nullable(),
      errorsTotal: z.number(),
      differencesTotal: z.number(),
    }),
    totalPickAndPack: z.object({
      expectedQuantity: z.number().nullable(),
      expectedRate: z.number().nullable(),
      invoicedQuantity: z.number().nullable(),
      invoicedRate: z.number().nullable(),
      errorsTotal: z.number(),
      differencesTotal: z.number(),
    }),
    // Deprecated, will be removed once Order Details 2.0 goes live
    totalPickFees: z.object({
      expectedQuantity: z.number().nullable(),
      expectedRate: z.number().nullable(),
      invoicedQuantity: z.number().nullable(),
      invoicedRate: z.number().nullable(),
      errorsTotal: z.number(),
      differencesTotal: z.number(),
    }),
  }),
  status: z.nativeEnum(SalesOrderStatus),
  totalCostSummary: z.object({
    errorsTotal: z.number(),
    differencesTotal: z.number(),
    expectedRate: z.number(),
    invoicedRate: z.number(),
  }),
  type: z.string(),
  errorGroupDescriptions: z.array(z.string()),
  relatedInvoices: z.array(z.string()),
});

export const ErrorStatusUpdateSchema = z.object({
  salesOrderNumber: z.string(),
  errorType: z.string().optional(),
  errorTypeToExclude: z.string().optional(),
  serviceType: z.string(),
  status: z.nativeEnum(SalesOrderErrorStatus),
});

export const ErrorStatusUpdateV2Schema = z.object({
  invoiceNumber: z.string(),
  salesOrderNumber: z.string(),
  errorType: z.string().optional(),
  errorTypeToExclude: z.string().optional(),
  serviceType: z.string(),
  status: z.nativeEnum(SalesOrderErrorStatus),
});

export const AcceptedOrderErrorSchema = z.object({
  errorType: z.string(),
  serviceType: z.string(),
  errorsCount: z.number(),
  errorsDollars: z.number(),
});
export type AcceptedOrderErrorSchema = z.infer<typeof AcceptedOrderErrorSchema>;

export const ErrorInfoSchema = z.object({
  unfavorableErrorsCount: z.number(),
  unfavorableErrorsDollars: z.number(),
  unfavorableErrorsRejectedCount: z.number(),
  unfavorableErrorsRejectedDollars: z.number(),
  favorableErrorsCount: z.number(),
  favorableErrorsDollars: z.number(),
  creditRequestedErrorsCount: z.number(),
  creditRequestedErrorsDollars: z.number(),
  unfavorableOrderCount: z.number(),
  favorableOrderCount: z.number(),
  neutralOrderCount: z.number(),
  totalErrorStatuses: z.object({
    accepted: z.number(),
    rejected: z.number(),
    sent: z.number(),
    unreviewed: z.number(),
  }),
  totalErrorsCount: z.number(),
  services: z.array(z.string()).optional(),
});
export type ErrorInfoSchema = z.infer<typeof ErrorInfoSchema>;

export interface SalesOrderWithErrors {
  salesOrderNumber: string;
  invoiceNumber: string;
  brandId: string;
  errors: SalesOrderError[];
}

export const ServiceChargeSchema = z.object({
  invoice_number: z.string(),
  item: z.string().nullable(),
  order_number: z.string(),
  service: z.string(),
  error: z.string(),
  rate: z.number().nullable().transform(Number),
  quantity: z.number(),
  quantity_uom: z.string().nullable(),
  total_cost: z.number(),
});

export const ReconciledServiceChargeSchema = z.object({
  invoice_number: z.string(),
  order_number: z.string(),
  service: z.string(),
  error: z.string().nullable(),
  expected_item: z.string().nullable(),
  expected_rate: z.number().nullable().transform(Number),
  expected_quantity: z.number(),
  expected_quantity_uom: z.string().nullable(),
  expected_total_cost: z.number(),
  actual_item: z.string().nullable(),
  actual_rate: z.number().nullable().transform(Number),
  actual_quantity: z.number(),
  actual_quantity_uom: z.string().nullable(),
  actual_total_cost: z.number(),
  error_total: z.number(),
});

export const CreditRequestExtractRowSchema = ReconciledServiceChargeSchema.merge(
  z.object({
    error_group_identifier: z.string(),
    carrier: z.string().nullable(),
    expected_weight: z.number().nullable(),
    actual_weight: z.number().nullable(),
    expected_zone: z.string().nullable(),
    actual_zone: z.string().nullable(),
  }),
);

export const ClientSalesOrderErrorServiceCharge = z.object({
  related_service_charge_ids: z.array(
    z.object({
      related_service_charges: z.object({
        real_service_charges: z
          .array(
            z.object({
              real_service_charge: ServiceChargeSchema,
            }),
          )
          .optional(),
        synthetic_service_charges: z
          .array(
            z.object({
              synthetic_service_charge: ServiceChargeSchema,
            }),
          )
          .optional(),
        reconciled_service_charges: z
          .array(
            z.object({
              reconciled_service_charge: ReconciledServiceChargeSchema,
            }),
          )
          .optional(),
      }),
    }),
  ),
});

export const CDMSalesOrderErrorSchema = z.object({
  sales_order_number: z.string(),
  brand_id: z.string(),
  invoice_number: z.string(),
  reconciliation_run_id: z.string(),
  related_service_charge_ids: ClientSalesOrderErrorServiceCharge.nullish(),
  difference_root_cause: z.string().nullable(),
  service: z.string(),
  total_difference_amount: z.string().transform(Number),
  total_unfavorable_amount: z.string().transform(Number),
  total_favorable_amount: z.string().transform(Number),
  unfavorable_count: z.string().transform(Number),
  favorable_count: z.string().transform(Number),
  total_error_count: z.string().transform(Number),
  scenario_metadata: z.record(z.unknown()).nullish(),
});

export const AcceptedSelectSalesOrderErrors = z.object({
  id: z.string().uuid(),
  salesOrderNumber: z.string(),
  errorType: z.string(),
  serviceType: z.string(),
  service: z.string(),
  related_service_charge_ids: ClientSalesOrderErrorServiceCharge,
  totalErrorsDollars: z.number(),
  totalErrorsCount: z.number(),
});

export type SelectedAcceptedSalesOrderErrors = Omit<
  RawAcceptedSelectSalesOrderErrors,
  'actualCharge' | 'expectedCharge' | 'carrier' | 'actualWeight' | 'expectedWeight' | 'actualZone' | 'expectedZone'
> &
  Partial<
    Pick<
      RawAcceptedSelectSalesOrderErrors,
      'actualCharge' | 'expectedCharge' | 'carrier' | 'actualWeight' | 'expectedWeight' | 'actualZone' | 'expectedZone'
    >
  >;

export type RawAcceptedSelectSalesOrderErrors = {
  actualCharge: string;
  expectedCharge: string;
  carrier?: string | undefined;
  actualWeight?: number | undefined;
  expectedWeight?: number | undefined;
  actualZone?: string | undefined;
  expectedZone?: string | undefined;
  id: string;
  salesOrderNumber: string;
  errorType: string;
  serviceType: string;
  service: string;
  related_service_charge_ids: ClientSalesOrderErrorServiceCharge;
  totalErrorsDollars: number;
  totalErrorsCount: number;
  errorGroupIdentifier: string;
};

export const AlternateSalesOrder = z.object({
  sales_order_number: z.string(),
  alternate_sales_order_number: z.string(),
  brand_id: z.string(),
  invoice_number: z.string(),
  type: z.string(),
});

export const ClientSalesOrderStatus = z.object({
  sales_order_number: z.string(),
  brand_id: z.string(),
  invoice_number: z.string(),
});

export const OrderLccMetadata = z.object({
  warehouse: z.string().optional(),
  carrier: z.string().optional(),
  serviceLevel: z.string().optional(),
  zone: z.string().optional(),
  weightChargedOz: z.string().optional(),
});
export type OrderLccMetadata = z.infer<typeof OrderLccMetadata>;
export const OrderLccSchema = z.object({
  isLccEnabled: z.boolean(),
  charges: z
    .object({
      type: z
        .nativeEnum(ScenarioTypes)
        .describe(`What kind of alternate scenario is this? i.e ${Object.values(ScenarioTypes).join(', ')}`),
      favorableDollarsTotal: z
        .number()
        .describe(
          "Difference amount we've identified between the invoiced amount and the least cost carrier amount. The larger the better.",
        ),
      unfavorableDollarsTotal: z
        .number()
        .describe(
          'Difference amount we have identified between the invoiced amount and the least cost carrier amount.',
        ),
      metadata: OrderLccMetadata.optional(),
    })
    .array(),
});
export const OrderLccAlternativeSchema = z.object({
  type: z
    .nativeEnum(ScenarioTypes)
    .describe(`What kind of alternate scenario is this? i.e ${Object.values(ScenarioTypes).join(', ')}`),
  scenarioName: z.string(),
  carrier: z.string(),
  serviceLevel: z.string(),
  zone: z.string(),
  expectedCost: z.number(),
});

export const OrderSummaryDetailsSchema = z.object({
  id: z.string(),
  brand_id: z.string(),
  invoice_number: z.string(),
  sales_order_number: z.string(),
  sales_order_type: z.string(),
  level: z.string(),
  expected_total: z.string(),
  invoiced_total: z.string(),
  error: z.string().nullable(),
  difference: z.string(),
  error_count: z.string().transform(Number),
});

export const OrderDetailsRowSchema = z.object({
  id: z.string(),
  brand_id: z.string(),
  invoice_number: z.string(),
  fulfillment_id: z.string().nullable(),
  sales_order_number: z.string(),
  sales_order_type: z.string(),
  level: z.string(),
  sku: z.string().nullable(),
  product_name: z.string().nullable(),
  reconciled_service_charge_id: z.string().nullable(),
  service_code: z.string().nullable(),
  expected_quantity: z.string().nullable(),
  expected_rate: z.string().nullable(),
  expected_total: z.string().nullable(),
  invoiced_quantity: z.string().nullable(),
  invoiced_rate: z.string().nullable(),
  invoiced_total: z.string().nullable(),
  error: z.string().nullable(),
  difference: z.string().nullable(),
  alternate_sales_order_numbers: z
    .array(z.string().nullable())
    .nullable()
    .transform((val) => (val ? val.filter((item) => item !== null) : val)),
});

export const FreightLccAlternativeSchema = z.object({
  invoiceNumber: z.string(),
  salesOrderNumber: z.string(),
  scenarioName: z.string(),
  carrier: z.string(),
  serviceLevel: z.string(),
  zone: z.string(),
  expectedCost: z.number(),
});

// Schema for Order Details 2.0 endpoint
export const OrderDetailsSchema = z.object({
  invoiceNumber: z.string(),
  status: z.nativeEnum(SalesOrderStatus),
  isInvoiceRollupOrder: z.boolean().optional(),
  isNoSalesOrderMatch: z.boolean().default(false),
  logisticsProvider: z.string(),
  warehouse: z.string(),
  type: z.string(),
  labelCreateTime: z.date().nullable(),
  errorGroupDescriptions: z.array(z.string()),
  relatedInvoices: z.array(z.string()),
  freight: z
    .object({
      status: z.nativeEnum(SalesOrderStatusByServiceType).nullable().optional(),
      totalFreight: z.object({
        expectedRate: z.number(),
        invoicedRate: z.number(),
        errorsTotal: z.number(),
        differencesTotal: z.number(),
      }),
      expected: z.object({
        total: z.object({
          cost: z.number(),
        }),
        serviceFees: z.array(
          z.object({
            serviceName: z.string(),
            qty: z.number(),
            rate: z.number().optional(),
            totalCost: z.number(),
            skus: z.array(
              z.object({
                name: z.string(),
                qty: z.number(),
                totalCost: z.number(),
              }),
            ),
          }),
        ),
      }),
      invoiced: z.object({
        total: z.object({
          cost: z.number(),
        }),
        serviceFees: z.array(
          z.object({
            serviceName: z.string(),
            qty: z.number(),
            rate: z.number().optional(),
            totalCost: z.number(),
            skus: z.array(
              z.object({
                name: z.string(),
                qty: z.number(),
                totalCost: z.number(),
              }),
            ),
          }),
        ),
      }),
      details: FreightDetailsSchema,
    })
    .optional(),
  fulfillment: z
    .object({
      status: z.nativeEnum(SalesOrderStatusByServiceType).nullable().optional(),
      totalPickAndPack: z.object({
        expectedRate: z.number(),
        invoicedRate: z.number(),
        errorsTotal: z.number(),
        differencesTotal: z.number(),
      }),
      expected: z.object({
        total: z.object({
          cost: z.number(),
        }),
        serviceFees: z.array(
          z.object({
            serviceName: z.string(),
            qty: z.number(),
            rate: z.number(),
            totalCost: z.number(),
            skus: z.array(
              z.object({
                name: z.string(),
                qty: z.number(),
                rate: z.number().optional(),
                totalCost: z.number(),
              }),
            ),
          }),
        ),
      }),
      invoiced: z.object({
        total: z.object({
          cost: z.number(),
        }),
        serviceFees: z.array(
          z.object({
            serviceName: z.string(),
            qty: z.number(),
            rate: z.number(),
            totalCost: z.number(),
            skus: z.array(
              z.object({
                name: z.string(),
                qty: z.number(),
                rate: z.number().optional(),
                totalCost: z.number(),
              }),
            ),
          }),
        ),
      }),
    })
    .optional(),
  wholesaleFreight: z
    .object({
      status: z.nativeEnum(SalesOrderStatusByServiceType).nullable().optional(),
      totalWholesaleFreight: z.object({
        expectedRate: z.number(),
        invoicedRate: z.number(),
        errorsTotal: z.number(),
        differencesTotal: z.number(),
      }),
      expected: z.object({
        total: z.object({
          cost: z.number(),
        }),
        serviceFees: z.array(
          z.object({
            serviceName: z.string(),
            qty: z.number(),
            rate: z.number().optional(),
            totalCost: z.number(),
            skus: z.array(
              z.object({
                name: z.string(),
                qty: z.number(),
                totalCost: z.number(),
              }),
            ),
          }),
        ),
      }),
      invoiced: z.object({
        total: z.object({
          cost: z.number(),
        }),
        serviceFees: z.array(
          z.object({
            serviceName: z.string(),
            qty: z.number(),
            rate: z.number().optional(),
            totalCost: z.number(),
            skus: z.array(
              z.object({
                name: z.string(),
                qty: z.number(),
                totalCost: z.number(),
              }),
            ),
          }),
        ),
      }),
      details: FreightDetailsSchema,
      orderFees: z.array(
        z.object({
          service: z.string(),
          serviceType: z.string(),
          invoicedQuantity: z.number(),
          invoicedRate: z.number(),
          invoicedTotalCharges: z.number(),
          expectedQuantity: z.number(),
          expectedRate: z.number(),
          expectedTotalCharges: z.number(),
          errorDifference: z.number(),
          errorType: z.string().nullable(),
        }),
      ) satisfies z.ZodType<OrderInsertFeeWithServiceType[]>,
    })
    .optional(),
});

export const FreightPackageDetailsSchema = z.object({
  invoiceNumber: z.string(),
  salesOrderNumber: z.string(),
  labelCreateTime: z.date().nullable(),
  shipTime: z.date().nullable(),
  deliveryTime: z.date().nullable(),
  invoicedCarrier: z.string().nullable(),
  invoicedServiceLevel: z.string().nullable(),
  invoicedZone: z.string().nullable(),
  invoicedActualWeight: z.string().nullable(),
  invoicedRoundedWeight: z.string().nullable(),
  invoicedDimensionalWeight: z.string().nullable(),
  invoicedBilledWeight: z.string().nullable(),
  invoicedVirtualCarrier: z.string().nullable(),
  invoicedVirtualCarrierServiceLevel: z.string().nullable(),
  invoicedZipcode: z.string().nullable(),
  expectedCarrier: z.string().nullable(),
  expectedServiceLevel: z.string().nullable(),
  expectedZone: z.string().nullable(),
  expectedActualWeight: z.string().nullable(),
  expectedRoundedWeight: z.string().nullable(),
  expectedDimensionalWeight: z.string().nullable(),
  expectedBilledWeight: z.string().nullable(),
  expectedZipcode: z.string().nullable(),
  expectedShipToCity: z.string().nullable(),
  expectedShipToState: z.string().nullable(),
  expectedRecipient: z.string().nullable(),
  expectedVirtualCarrier: z.string().nullable(),
  expectedVirtualCarrierServiceLevel: z.string().nullable(),
  expectedPackageType: z.string().nullable(),
  invoicedQuantityByUom: z.array(z.object({ uom: z.string(), quantity: z.number() })).nullable(),
  expectedQuantityByUom: z.array(z.object({ uom: z.string(), quantity: z.number() })).nullable(),
});

export type OrderSummaryDetails = z.infer<typeof OrderSummaryDetailsSchema>;
export type OrderDetailsRow = z.infer<typeof OrderDetailsRowSchema>;
export type ClientSalesOrderStatus = z.infer<typeof ClientSalesOrderStatus>;
export type AcceptedSelectSalesOrderErrors = z.infer<typeof AcceptedSelectSalesOrderErrors>;
export type ClientSalesOrderErrorServiceCharge = z.infer<typeof ClientSalesOrderErrorServiceCharge>;
export type ServiceCharge = z.infer<typeof ServiceChargeSchema>;
export type ReconciledServiceCharge = z.infer<typeof ReconciledServiceChargeSchema>;
export type AlternateSalesOrder = z.infer<typeof AlternateSalesOrder>;
export type CDMSalesOrderError = z.infer<typeof CDMSalesOrderErrorSchema>;
export type CDMSalesOrderErrorWithFlag = CDMSalesOrderError & { is_invoice_roll_up_item: boolean };
export type SalesOrderQuery = z.infer<typeof SalesOrderQuerySchema>;
export type InvoiceSalesOrder = z.infer<typeof InvoiceSalesOrderSchema>;
export type SalesOrder = z.infer<typeof SalesOrderSchema>;
export type QueryMetaData = z.infer<typeof QueryMetaDataSchema>;
export type SalesOrderError = z.infer<typeof SalesOrderErrorsSchema>;
export type ErrorStatusUpdate = z.infer<typeof ErrorStatusUpdateSchema>;
export type ErrorStatusUpdateV2 = z.infer<typeof ErrorStatusUpdateV2Schema>;
export type OrderOverview = z.infer<typeof OrderOverviewSchema>;
export type OrderLccSchema = z.infer<typeof OrderLccSchema>;
export type ErrorGroupFilter = z.infer<typeof ErrorGroupFilterSchema>;
export type InvoiceErrorGroupSalesOrder = z.infer<typeof InvoiceErrorGroupSalesOrderSchema>;
export type SalesOrderErrorGroupQuery = z.infer<typeof SalesOrderErrorGroupQuerySchema>;
export type SalesOrderErrorGroupQueryV2 = z.infer<typeof SalesOrderErrorGroupQueryV2Schema>;
export type OrderLccAlternative = z.infer<typeof OrderLccAlternativeSchema>;
export type FreightLccAlternative = z.infer<typeof FreightLccAlternativeSchema>;
export type FreightPackageDetails = z.infer<typeof FreightPackageDetailsSchema>;
export type SalesOrderMetadata = z.infer<typeof SalesOrderMetadataSchema>;
export type CreditRequestExtractRow = z.infer<typeof CreditRequestExtractRowSchema>;
export type OrderDetails = z.infer<typeof OrderDetailsSchema>;
export type FreightDetailsType = z.infer<typeof FreightDetailsSchema>;
